/**
 * Pluggable snapshot store backing cart backup + warehouse-cart preservation.
 *
 * `mode: 'single'` — storage[key] = { version, [timestampField], ...payload }.
 *   Used by backup.ts: one entry per storage key.
 * `mode: 'map'`    — storage[key] = { [mapKey]: entry, ... }.
 *   Used by warehouse-cart-preservation: many entries keyed by warehouseId.
 *
 * Auto-evicts entries on `get` when they fail validation (wrong version,
 * past-TTL, future-timestamp). In `map` mode, `get` also sweeps any expired
 * sibling entries in the same read+write pass — callers do not need a
 * separate `cleanupExpired` call before consuming.
 *
 * @module shared-cart/cart/snapshot
 */

interface SnapshotStoreOptions {
  storage: 'session' | 'local';
  key: string;
  mode?: string;
  ttlMs: number;
  version: number;
  timestampField?: string;
}

export interface SnapshotEntry {
  version: number;
  [key: string]: unknown;
}

/**
 * @param {Object} options
 * @param {'session'|'local'} options.storage
 * @param {string} options.key
 * @param {'single'|'map'} options.mode
 * @param {number} options.ttlMs
 * @param {number} options.version
 * @param {string} [options.timestampField='savedAt']
 */
export function createSnapshotStore({
  storage,
  key,
  mode,
  ttlMs,
  version,
  timestampField = 'savedAt',
}: SnapshotStoreOptions) {
  if (mode !== 'single' && mode !== 'map') {
    throw new Error(`createSnapshotStore: mode must be 'single' or 'map' (got ${String(mode)})`);
  }
  const storageObj = storage === 'session' ? sessionStorage : localStorage;

  function readMap(): Record<string, unknown> | null {
    try {
      const raw = storageObj.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  function writeMap(map: Record<string, unknown>): boolean {
    try {
      storageObj.setItem(key, JSON.stringify(map));
      return true;
    } catch {
      return false;
    }
  }

  function removeKey(): void {
    try {
      storageObj.removeItem(key);
    } catch {
      // ignore
    }
  }

  function isValid(entry: unknown): entry is SnapshotEntry {
    if (!entry || typeof entry !== 'object') return false;
    if ((entry as SnapshotEntry).version !== version) return false;
    const stamp = (entry as Record<string, unknown>)[timestampField];
    if (typeof stamp !== 'number') return false;
    const now = Date.now();
    if (stamp > now) return false;
    if (now - stamp >= ttlMs) return false;
    return true;
  }

  function save(payload: Record<string, unknown>, mapKey?: string): boolean {
    const entry = {
      ...payload,
      version,
      [timestampField]: Date.now(),
    };

    if (mapKey === undefined) {
      return writeMap(entry);
    }

    const current = readMap() || {};
    current[mapKey] = entry;
    return writeMap(current);
  }

  function get(mapKey?: string): SnapshotEntry | null {
    const data = readMap();
    if (!data) return null;

    if (mode === 'single') {
      if (isValid(data)) return data;
      removeKey();
      return null;
    }

    // Map mode: in a single read+write pass, sweep any expired siblings AND
    // evict the target entry if invalid. Saves a separate cleanupExpired call.
    let mutated = false;
    for (const k of Object.keys(data)) {
      if (k === mapKey) continue;
      if (!isValid(data[k])) {
        delete data[k];
        mutated = true;
      }
    }

    const entry = data[mapKey!];
    const entryValid = entry !== null && entry !== undefined && isValid(entry);
    if (entry !== null && entry !== undefined && !entryValid) {
      delete data[mapKey!];
      mutated = true;
    }

    if (mutated) {
      if (Object.keys(data).length === 0) {
        removeKey();
      } else {
        writeMap(data);
      }
    }

    return entryValid ? entry : null;
  }

  function clear(mapKey?: string): boolean {
    if (mapKey === undefined) {
      try {
        storageObj.removeItem(key);
        return true;
      } catch {
        return false;
      }
    }
    const data = readMap();
    if (!data) return true;
    if (!(mapKey in data)) return true;
    delete data[mapKey];
    try {
      if (Object.keys(data).length === 0) {
        storageObj.removeItem(key);
      } else {
        storageObj.setItem(key, JSON.stringify(data));
      }
      return true;
    } catch {
      return false;
    }
  }

  function cleanupExpired(): void {
    if (mode === 'single') return;
    const data = readMap();
    if (!data) return;

    let mutated = false;
    for (const k of Object.keys(data)) {
      if (!isValid(data[k])) {
        delete data[k];
        mutated = true;
      }
    }
    if (!mutated) return;
    if (Object.keys(data).length === 0) {
      removeKey();
    } else {
      writeMap(data);
    }
  }

  return { save, get, isValid, clear, cleanupExpired };
}
